import React, { useEffect, useState } from "react";
import { Prompt, useHistory, useLocation } from "react-router-dom";
import { notify } from "../../components-ui/toast";
import AddressCard from "./components/AddressCard";
import ContactCard, {
  getContactValidationErrors,
  getPersistedContactMethods,
} from "./components/ContactCard";
import HouseholdCard from "./components/HouseholdCard";
import ServiceEnrollmentCard, {
  createServiceEnrollmentsForParty,
  ServiceRoleSelections,
} from "./components/ServiceEnrollmentCard";
import EmergencyContactCard, {
  hasCompleteEmergencyContact,
} from "./components/EmergencyContactCard";
import PartyQualificationsCard from "./components/PartyQualificationsCard";
import PartyDetailsCard from "./components/PartyDetailsCard";
import {
  PartyAddress,
  ContactMethod,
  PersonDetails,
  ContactPreference,
  HouseholdMembership,
  RegistrationMemberType,
  ServiceRegistrationRequirementDto,
  ServiceRoleDto,
} from "../../types/types";
import { api } from "../../../api/apiClient";
import { useAuth } from "../../../context/AuthContext";
import { useStaticData } from "../../../context/StaticDataContext";
import "../../../css/forms.css";
import "../../../css/layout.css";
import "../../../css/buttons.css";
import { PartyFormMode, usePartyFormContext } from "./PartyFormContext";

interface PartyPersonProps {
  partyId: number | null;
  resetSignal: number;
  onSaved?: (partyId: number) => void;
  onCancel?: () => void;
  showCancel?: boolean;
  saveLabel?: string;
  cancelLabel?: string;
  initialDetails?: PersonDetails;
  onCompleted?: () => void;
  agreementsContent?: React.ReactNode;
  consentsContent?: React.ReactNode;
}

const initialPersonDetails: PersonDetails = {
  partyId: 0,
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  gender: "",
  partyAddresses: [],
  contactMethods: [],
  contactPreferences: [],
  householdMemberships: [],
  emergencyContact: null,
};

const registrationMemberTypes: RegistrationMemberType[] = [
  "ServiceUser",
  "PrimaryContact",
  "Volunteer",
  "Staff",
];

function getRegistrationMemberType(value: string | null): RegistrationMemberType {
  return registrationMemberTypes.includes(value as RegistrationMemberType)
    ? (value as RegistrationMemberType)
    : "ServiceUser";
}

const toComparablePerson = (details: PersonDetails): PersonDetails => ({
  ...details,
  dateOfBirth: details.dateOfBirth?.trim() || "",
  partyAddresses: details.partyAddresses ?? [],
  contactMethods: getPersistedContactMethods(details.contactMethods ?? []),
  contactPreferences: details.contactPreferences ?? [],
  householdMemberships: details.householdMemberships ?? [],
});

const PartyPerson: React.FC<PartyPersonProps> = ({
  partyId,
  resetSignal,
  onSaved,
  onCancel,
  showCancel = false,
  saveLabel = "Save",
  cancelLabel = "Cancel",
  initialDetails,
  onCompleted,
  agreementsContent,
  consentsContent,
}) => {
  const partyContext = usePartyFormContext();
  const history = useHistory();
  const location = useLocation();
  const [formMode, setFormMode] = useState<"NEW" | "UPDATE">("NEW");
  const [personDetails, setPersonDetails] =
    useState<PersonDetails>(initialPersonDetails);

  const [originalPerson, setOriginalPerson] =
    useState<PersonDetails>(initialPersonDetails);

  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [suppressUnsavedPrompt, setSuppressUnsavedPrompt] = useState(false);
  const [possibleDuplicatePeople, setPossibleDuplicatePeople] = useState<
    Array<{ partyId: number; displayName: string; dateOfBirth: string | null }>
  >([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
  const [originalSelectedServiceIds, setOriginalSelectedServiceIds] = useState<
    number[]
  >([]);
  const [selectedServiceRoles, setSelectedServiceRoles] =
    useState<ServiceRoleSelections>({});
  const [originalSelectedServiceRoles, setOriginalSelectedServiceRoles] =
    useState<ServiceRoleSelections>({});
  const { selectedTenant } = useAuth();
  const { data } = useStaticData();
  const contactTypes = data?.contactTypes ?? [];

  useEffect(() => {
    if (partyContext.mode !== PartyFormMode.Admin || formMode !== "NEW" || partyId ||
      !personDetails.firstName.trim() || !personDetails.lastName.trim() ||
      !personDetails.dateOfBirth?.trim()) {
      setPossibleDuplicatePeople([]);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      const query = new URLSearchParams({
        firstName: personDetails.firstName.trim(),
        lastName: personDetails.lastName.trim(),
        dateOfBirth: personDetails.dateOfBirth.trim().slice(0, 10),
      });
      api<Array<{ partyId: number; displayName: string; dateOfBirth: string | null }>>(
        `/api/duplicate-parties/check-person?${query.toString()}`,
        { suppressLoading: true, notifyError: false },
      )
        .then((matches) => { if (!cancelled) setPossibleDuplicatePeople(matches); })
        .catch(() => { if (!cancelled) setPossibleDuplicatePeople([]); });
    }, 450);

    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [partyContext.mode, formMode, partyId, personDetails.firstName, personDetails.lastName, personDetails.dateOfBirth]);

  const tenantId = selectedTenant?.tenantId;
  const queryParams = new URLSearchParams(location.search);
  const registrationMemberType = getRegistrationMemberType(
    queryParams.get("memberType"),
  );
  const registrationServiceIdValue = Number(
    queryParams.get("serviceId"),
  );
  const registrationServiceId = Number.isFinite(registrationServiceIdValue) &&
    registrationServiceIdValue > 0
    ? registrationServiceIdValue
    : partyContext.serviceId ?? null;
  const enrollmentSource =
    queryParams.get("source") === "Manual"
      ? "Manual"
      : registrationServiceId
        ? "Registration"
        : "Manual";
  const [registrationRequirements, setRegistrationRequirements] = useState<
    ServiceRegistrationRequirementDto[]
  >([]);
  const [registrationServiceRoleCount, setRegistrationServiceRoleCount] =
    useState(0);
  const hasRegistrationConfiguration = Boolean(
    registrationServiceId || partyContext.registrationRequirements,
  );
  const getRequirementLevel = (componentKey: string) =>
    registrationRequirements.find(
      (requirement) =>
        requirement.componentKey.toLowerCase() === componentKey.toLowerCase(),
    )?.requirementLevel;
  const getComponentOrder = (componentKey: string, fallback: number) =>
    registrationRequirements.find(
      (requirement) =>
        requirement.componentKey.toLowerCase() === componentKey.toLowerCase(),
    )?.displayOrder ?? fallback;
  const isComponentRequired = (componentKey: string) =>
    getRequirementLevel(componentKey) === "Required";
  const isComponentVisible = (
    componentKey: string,
    visibleWithoutConfiguration = true,
  ) => {
    if (!hasRegistrationConfiguration) return visibleWithoutConfiguration;

    return getRequirementLevel(componentKey) !== "Hidden";
  };
  const emergencyContactRequired = isComponentRequired("EmergencyContact");
  const showAddress = isComponentVisible("Address");
  const showContactDetails = isComponentVisible("ContactDetails");
  const showHousehold = isComponentVisible("Household");
  const showServiceEnrollment = isComponentVisible("ServiceMembership");
  const showEmergencyContact = isComponentVisible(
    "EmergencyContact",
    Boolean(personDetails.emergencyContact),
  );

  /* =========================
     Detect dirty state
  ========================= */

  useEffect(() => {
    const personDirty =
      JSON.stringify(toComparablePerson(personDetails)) !==
      JSON.stringify(toComparablePerson(originalPerson));
    const servicesDirty =
      formMode === "NEW" &&
      JSON.stringify([...selectedServiceIds].sort()) !==
        JSON.stringify([...originalSelectedServiceIds].sort());
    const rolesDirty =
      formMode === "NEW" &&
      JSON.stringify(selectedServiceRoles) !==
        JSON.stringify(originalSelectedServiceRoles);

    const agreementsDirty =
      partyContext.mode !== PartyFormMode.Admin &&
      Boolean(partyContext.agreementResponses?.some((item) => item.accepted !== null));

    setIsDirty(personDirty || servicesDirty || rolesDirty || agreementsDirty);
  }, [
    formMode,
    originalPerson,
    originalSelectedServiceIds,
    originalSelectedServiceRoles,
    personDetails,
    selectedServiceIds,
    selectedServiceRoles,
    partyContext.agreementResponses,
    partyContext.mode,
  ]);

  useEffect(() => {
    if (!registrationServiceId) {
      setRegistrationRequirements([]);
      setRegistrationServiceRoleCount(0);
      return;
    }

    if (partyContext.registrationRequirements) {
      setRegistrationRequirements(partyContext.registrationRequirements);
      setRegistrationServiceRoleCount(0);
      return;
    }

    let cancelled = false;

    async function loadRegistrationConfiguration() {
      try {
        const [requirements, roles] = await Promise.all([
          api<ServiceRegistrationRequirementDto[]>(
            `/api/services/${registrationServiceId}/registration-requirements?memberType=${registrationMemberType}`,
            { suppressLoading: true, notifyError: false },
          ),
          api<ServiceRoleDto[]>(`/api/services/${registrationServiceId}/roles`, {
            suppressLoading: true,
            notifyError: false,
          }),
        ]);

        if (cancelled) return;

        setRegistrationRequirements(requirements ?? []);
        setRegistrationServiceRoleCount(
          (roles ?? []).filter((role) => role.isActive).length,
        );
      } catch (error) {
        console.error("Failed to load registration configuration", error);
        if (!cancelled) {
          setRegistrationRequirements([]);
          setRegistrationServiceRoleCount(0);
        }
      }
    }

    loadRegistrationConfiguration();

    return () => {
      cancelled = true;
    };
  }, [
    partyContext.registrationRequirements,
    registrationMemberType,
    registrationServiceId,
  ]);

  /* =========================
     Warn before browser refresh
  ========================= */

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirty) return;

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  /* =========================
     Reset form
  ========================= */

  useEffect(() => {
    setPersonDetails(initialPersonDetails);
    setOriginalPerson(initialPersonDetails);
    setSelectedServiceIds([]);
    setOriginalSelectedServiceIds([]);
    setSelectedServiceRoles({});
    setOriginalSelectedServiceRoles({});
    setFormMode("NEW");
  }, [resetSignal]);

  /* =========================
     Load person
  ========================= */

  useEffect(() => {
    if (!initialDetails) return;
    setPersonDetails(initialDetails);
    setOriginalPerson(structuredClone(initialDetails));
    setFormMode("UPDATE");
  }, [initialDetails]);

  useEffect(() => {
    if (!partyId) return;

    const loadPerson = async () => {
      try {
        setLoading(true);

        const data = await api<PersonDetails>(`/api/person/get-by-id`, {
          method: "POST",
          body: partyId,
        });

        setPersonDetails(data);
        setOriginalPerson(structuredClone(data));

        setFormMode("UPDATE");
      } catch (error) {
        console.error("Failed to load person", error);
      } finally {
        setLoading(false);
      }
    };

    loadPerson();
  }, [partyId]);

  /* =========================
     Handlers
  ========================= */

  const handleAddressChange = (partyAddresses: PartyAddress[]) => {
    setPersonDetails((prev) => ({ ...prev, partyAddresses }));
  };

  const handleContactChange = (contactMethods: ContactMethod[]) => {
    setPersonDetails((prev) => ({ ...prev, contactMethods }));
  };

  const handleConsentChange = (contactPreferences: ContactPreference[]) => {
    setPersonDetails((prev) => ({ ...prev, contactPreferences }));
  };

  const handleHouseholdChange = (
    householdMemberships: HouseholdMembership[],
  ) => {
    setPersonDetails((prev) => ({ ...prev, householdMemberships }));
  };

  /* =========================
     Submit
  ========================= */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isDirty) return;

    const contactErrors = showContactDetails
      ? getContactValidationErrors(personDetails.contactMethods, contactTypes)
      : [];

    if (contactErrors.length > 0) {
      notify.error("Please correct invalid email addresses or mobile numbers before saving.");
      return;
    }

    if (
      isComponentRequired("ContactDetails") &&
      getPersistedContactMethods(personDetails.contactMethods).length === 0
    ) {
      notify.error("Please add at least one contact method before saving.");
      return;
    }

    if (
      isComponentRequired("Address") &&
      personDetails.partyAddresses.length === 0
    ) {
      notify.error("Please add an address before saving.");
      return;
    }

    if (
      isComponentRequired("Household") &&
      personDetails.householdMemberships.length === 0
    ) {
      notify.error("Please add household details before saving.");
      return;
    }

    const hasInvalidHousehold =
      showHousehold &&
      personDetails.householdMemberships.some(
        (membership) =>
          membership.household && membership.household.totalAdults < 1,
      );

    if (hasInvalidHousehold) {
      notify.error("A household must have at least one adult.");
      return;
    }

    if (
      emergencyContactRequired &&
      !hasCompleteEmergencyContact(personDetails.emergencyContact)
    ) {
      notify.error(
        "Please enter the emergency contact's name, relationship and a phone number or email address.",
      );
      return;
    }

    const serviceRolesRequired = registrationRequirements.some(
      (requirement) =>
        requirement.componentKey === "ServiceRoles" &&
        requirement.requirementLevel === "Required",
    );

    if (
      formMode === "NEW" &&
      registrationServiceId &&
      serviceRolesRequired &&
      registrationServiceRoleCount > 0 &&
      (selectedServiceRoles[registrationServiceId] ?? []).length === 0
    ) {
      notify.error("Please select at least one role before saving.");
      return;
    }

    if (
      partyContext.mode === PartyFormMode.Admin &&
      formMode === "NEW" &&
      registrationServiceId &&
      isComponentRequired("ServiceMembership") &&
      !selectedServiceIds.includes(registrationServiceId)
    ) {
      notify.error("Please add the service membership before saving.");
      return;
    }

    try {
      const dto = {
        ...personDetails,
        contactMethods: getPersistedContactMethods(
          personDetails.contactMethods,
        ),
        dateOfBirth:
          personDetails.dateOfBirth?.trim() !== ""
            ? personDetails.dateOfBirth
            : null,
      };

      let savedPerson: PersonDetails;

      if (partyContext.mode === PartyFormMode.PublicRegistration) {
        await api<void>("/api/public/register", {
          method: "POST",
          auth: "none",
          body: {
            partyType: "Person",
            serviceId: registrationServiceId,
            memberType: registrationMemberType,
            person: dto,
            agreementResponses: partyContext.agreementResponses ?? [],
          },
        });
        notify.success("Registration completed successfully");
        setSuppressUnsavedPrompt(true);
        setIsDirty(false);
        onCompleted?.();
        return;
      }

      if (partyContext.mode === PartyFormMode.ProfileCompletion) {
        await api<void>(
          `/api/public/profile/${encodeURIComponent(partyContext.invitationToken ?? "")}`,
          {
            method: "PUT",
            auth: "none",
            body: {
              person: dto,
              agreementResponses: partyContext.agreementResponses ?? [],
            },
          },
        );
        notify.success("Profile completed successfully");
        setSuppressUnsavedPrompt(true);
        setOriginalPerson(structuredClone(personDetails));
        setIsDirty(false);
        onCompleted?.();
        return;
      }

      if (formMode === "NEW") {
        savedPerson = await api("/api/person/create", {
          method: "POST",
          body: dto,
        });

        if (selectedServiceIds.length > 0) {
          await createServiceEnrollmentsForParty(
            savedPerson.partyId,
            selectedServiceIds,
            enrollmentSource,
            selectedServiceRoles,
            registrationMemberType,
          );
        }

        notify.success("Person created successfully");
        setFormMode("UPDATE");
      } else {
        savedPerson = await api("/api/person/update", {
          method: "POST",
          body: dto,
        });

        notify.success("Person updated successfully");
      }

      const safeSaved = toComparablePerson({
        ...savedPerson,
        partyAddresses:
          savedPerson.partyAddresses?.length || !dto.partyAddresses.length
            ? savedPerson.partyAddresses ?? []
            : dto.partyAddresses,
        contactMethods:
          savedPerson.contactMethods?.length || !dto.contactMethods.length
            ? savedPerson.contactMethods ?? []
            : dto.contactMethods,
        contactPreferences:
          savedPerson.contactPreferences?.length ||
          !dto.contactPreferences.length
            ? savedPerson.contactPreferences ?? []
            : dto.contactPreferences,
      });

      setSuppressUnsavedPrompt(true);
      setPersonDetails(safeSaved);
      setOriginalPerson(structuredClone(safeSaved));
      setOriginalSelectedServiceIds([...selectedServiceIds]);
      setOriginalSelectedServiceRoles(structuredClone(selectedServiceRoles));
      setIsDirty(false);

      window.scrollTo({ top: 0, behavior: "smooth" });

      // allow React to update state before navigation
      setTimeout(() => {
        onSaved?.(savedPerson.partyId);
        setSuppressUnsavedPrompt(false);
      }, 0);
    } catch (error) {
      console.error("Failed to save person", error);
      notify.error("Failed to save person");
    }
  };

  /* =========================
     Render
  ========================= */

  const registrationSections: Array<{
    key: string;
    order: number;
    content: React.ReactNode;
  }> = [
    {
      key: "PrimaryContact",
      order: getComponentOrder("PrimaryContact", 10),
      content: isComponentVisible("PrimaryContact") ? (
        <>
          <PartyDetailsCard details={personDetails} onChange={setPersonDetails} />
          {possibleDuplicatePeople.length > 0 && (
            <div className="notification notification--warning" role="alert">
              <strong>Possible duplicate person found.</strong> Check the existing record before creating a new one.
              {possibleDuplicatePeople.map((match) => (
                <div key={match.partyId}>
                  <button type="button" className="action-button secondary compact" onClick={() => history.push(`/party/${match.partyId}`, { partyType: "Person" })}>
                    Review {match.displayName} (#{match.partyId})
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      ) : null,
    },
    {
      key: "ContactDetails",
      order: getComponentOrder("ContactDetails", 20),
      content: showContactDetails ? (
        <ContactCard
          contacts={personDetails.contactMethods}
          partyId={personDetails.partyId}
          onChange={handleContactChange}
          contactPreferences={personDetails.contactPreferences}
          onPreferenceChange={handleConsentChange}
          allowAddAnother={partyContext.mode === PartyFormMode.Admin}
        />
      ) : null,
    },
    {
      key: "Address",
      order: getComponentOrder("Address", 30),
      content: showAddress ? (
        <AddressCard
          partyId={personDetails.partyId}
          addresses={personDetails.partyAddresses}
          onChange={handleAddressChange}
          allowAddAnother={partyContext.mode === PartyFormMode.Admin}
        />
      ) : null,
    },
    {
      key: "Household",
      order: getComponentOrder("Household", 40),
      content:
        showHousehold && partyContext.mode !== PartyFormMode.ProfileCompletion ? (
          <HouseholdCard
            value={personDetails.householdMemberships}
            onChange={handleHouseholdChange}
            defaultLastName={personDetails.lastName}
            partyAddresses={personDetails.partyAddresses}
            adminMode={partyContext.mode === PartyFormMode.Admin}
          />
        ) : null,
    },
    {
      key: "EmergencyContact",
      order: getComponentOrder("EmergencyContact", 60),
      content: showEmergencyContact ? (
        <EmergencyContactCard
          value={personDetails.emergencyContact}
          required={emergencyContactRequired}
          onChange={(emergencyContact) =>
            setPersonDetails((prev) => ({ ...prev, emergencyContact }))
          }
        />
      ) : null,
    },
    {
      key: "ServiceMembership",
      order: getComponentOrder("ServiceMembership", 70),
      content:
        showServiceEnrollment && partyContext.mode === PartyFormMode.Admin ? (
          <ServiceEnrollmentCard
            partyId={formMode === "UPDATE" ? personDetails.partyId : null}
            tenantId={tenantId}
            selectedServices={selectedServiceIds}
            onSelectedServicesChange={setSelectedServiceIds}
            selectedServiceRoles={selectedServiceRoles}
            onSelectedServiceRolesChange={setSelectedServiceRoles}
            registrationServiceId={registrationServiceId}
            registrationServiceName={partyContext.serviceName}
            registrationMemberType={registrationMemberType}
            readonly={!partyContext.allowServiceSelection && !registrationServiceId}
          />
        ) : null,
    },
    {
      key: "Agreements",
      order: getComponentOrder("Agreements", 85),
      content:
        agreementsContent && isComponentVisible("Agreements")
          ? agreementsContent
          : null,
    },
    {
      key: "Consents",
      order: getComponentOrder("Consents", 86),
      content:
        consentsContent && isComponentVisible("Consents")
          ? consentsContent
          : null,
    },
    {
      key: "Review",
      order: getComponentOrder("Review", 1000),
      content:
        partyContext.mode !== PartyFormMode.Admin &&
        isComponentVisible("Review") ? (
          <div className="card">
            <div className="card-header">
              <div className="card-title">Review and submit</div>
            </div>
            <div className="form-help-text">
              Check the information above, then submit your registration. You
              can return to any section to make changes before submitting.
            </div>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <Prompt
        when={isDirty && !suppressUnsavedPrompt}
        message="You have unsaved changes. Are you sure you want to leave?"
      />

      <form onSubmit={handleSubmit} className="party-organisation-form">
        {registrationSections
          .filter((section) => section.content !== null)
          .sort((left, right) => left.order - right.order)
          .map((section) => (
            <React.Fragment key={section.key}>{section.content}</React.Fragment>
          ))}

        {partyContext.mode === PartyFormMode.Admin &&
          formMode === "UPDATE" &&
          personDetails.partyId > 0 && (
            <PartyQualificationsCard partyId={personDetails.partyId} />
          )}

        {/* =========================
           Save
        ========================= */}

        <div className="form-floating-actions">
          <button
            className={`form-submit-button ${
              isDirty ? "save-button-dirty" : ""
            }`}
            type="submit"
            disabled={!isDirty || loading}
          >
            {saveLabel}
          </button>

          {showCancel && (
            <button
              type="button"
              className="form-submit-button form-submit-button--secondary"
              onClick={onCancel}
            >
              {cancelLabel}
            </button>
          )}
        </div>
      </form>
    </>
  );
};

export default PartyPerson;
